import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Shield, Database, CreditCard, Cookie, UserCheck, Mail } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 to-zinc-900">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-950/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <span className="text-red-500 text-2xl">💪</span>
            <span className="font-bold text-white">T-Fit Inc Classes</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-12 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-500/10 rounded-full mb-6">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">Privacy Policy</h1>
          <p className="text-zinc-400">Last updated: February 2025</p>
        </div>

        <div className="space-y-10">
          {/* Introduction */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Introduction</h2>
            <p className="text-zinc-300 leading-relaxed">
              T-Fit Inc Classes (&ldquo;we,&rdquo; &ldquo;our,&rdquo; or &ldquo;us&rdquo;) is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our fitness class booking platform. Please read this policy carefully to 
              understand our practices regarding your personal data.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-white">Information We Collect</h2>
            </div>
            <div className="space-y-4 text-zinc-300">
              <div>
                <h3 className="font-medium text-white mb-2">Personal Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Name and email address</li>
                  <li>Phone number (optional)</li>
                  <li>Account credentials</li>
                  <li>Profile information</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">Booking Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Class reservations and attendance history</li>
                  <li>Preferred class types and schedules</li>
                  <li>Waitlist registrations</li>
                  <li>Reviews and ratings you provide</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-white mb-2">Technical Information</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>IP address and browser type</li>
                  <li>Device information</li>
                  <li>Usage data and analytics</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Payment Information */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-white">Payment Information</h2>
            </div>
            <div className="text-zinc-300 space-y-3">
              <p>
                We offer multiple payment options including PayPal and Pay by Cash. When you make 
                a payment through PayPal, your payment information is processed directly by PayPal 
                and is subject to their privacy policy.
              </p>
              <p>
                <strong className="text-white">We do not store:</strong> Credit card numbers, 
                bank account details, or other sensitive financial information on our servers.
              </p>
              <p>
                We retain transaction records including payment amounts, dates, and confirmation 
                numbers for accounting and customer service purposes.
              </p>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">How We Use Your Information</h2>
            <ul className="space-y-3 text-zinc-300">
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1">•</span>
                <span>Process and manage your class bookings and payments</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1">•</span>
                <span>Send booking confirmations, reminders, and waitlist notifications</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1">•</span>
                <span>Provide customer support and respond to inquiries</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1">•</span>
                <span>Improve our services and develop new features</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1">•</span>
                <span>Send promotional communications (with your consent)</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-red-500 mt-1">•</span>
                <span>Ensure security and prevent fraudulent activities</span>
              </li>
            </ul>
          </section>

          {/* Cookies */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-white">Cookies & Tracking</h2>
            </div>
            <div className="text-zinc-300 space-y-3">
              <p>
                We use cookies and similar tracking technologies to enhance your experience on our platform:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong className="text-white">Essential cookies:</strong> Required for authentication and security</li>
                <li><strong className="text-white">Functional cookies:</strong> Remember your preferences</li>
                <li><strong className="text-white">Analytics cookies:</strong> Help us understand how you use our platform</li>
              </ul>
              <p>
                You can control cookie preferences through your browser settings. Note that disabling 
                certain cookies may affect the functionality of our platform.
              </p>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Data Sharing & Disclosure</h2>
            <div className="text-zinc-300 space-y-3">
              <p>We may share your information with:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li><strong className="text-white">Service providers:</strong> Payment processors (PayPal), email services</li>
                <li><strong className="text-white">Instructors:</strong> Class attendance information for the classes you book</li>
                <li><strong className="text-white">Legal authorities:</strong> When required by law or to protect our rights</li>
              </ul>
              <p className="mt-4">
                <strong className="text-white">We do not sell</strong> your personal information to third parties for marketing purposes.
              </p>
            </div>
          </section>

          {/* Your Rights */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <UserCheck className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-white">Your Rights</h2>
            </div>
            <div className="text-zinc-300 space-y-3">
              <p>You have the right to:</p>
              <ul className="list-disc list-inside space-y-1 ml-2">
                <li>Access and receive a copy of your personal data</li>
                <li>Correct inaccurate or incomplete information</li>
                <li>Request deletion of your personal data</li>
                <li>Opt-out of marketing communications</li>
                <li>Withdraw consent for data processing</li>
              </ul>
              <p className="mt-4">
                To exercise these rights, please contact us using the information below or manage 
                your preferences in your account settings.
              </p>
            </div>
          </section>

          {/* Data Security */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Data Security</h2>
            <p className="text-zinc-300 leading-relaxed">
              We implement appropriate technical and organizational security measures to protect 
              your personal information against unauthorized access, alteration, disclosure, or 
              destruction. This includes encryption, secure servers, and regular security assessments. 
              However, no method of transmission over the Internet is 100% secure, and we cannot 
              guarantee absolute security.
            </p>
          </section>

          {/* Data Retention */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Data Retention</h2>
            <p className="text-zinc-300 leading-relaxed">
              We retain your personal information for as long as necessary to provide our services, 
              comply with legal obligations, resolve disputes, and enforce our agreements. When you 
              delete your account, we will delete or anonymize your personal information within 30 days, 
              except where we are required to retain it for legal purposes.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <Mail className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-white">Contact Us</h2>
            </div>
            <div className="text-zinc-300 space-y-2">
              <p>If you have questions about this Privacy Policy or our data practices, please contact us:</p>
              <p className="mt-4">
                <strong className="text-white">Email:</strong> admin@tfitinc.co.uk
              </p>
              <p>
                <strong className="text-white">Address:</strong> T-Fit Inc, 3 Wesley Road, Winchester, SO23 7PX
              </p>
            </div>
          </section>

          {/* Updates */}
          <section className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-6">
            <h2 className="text-xl font-semibold text-white mb-4">Policy Updates</h2>
            <p className="text-zinc-300 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new Privacy Policy on this page and updating the &ldquo;Last updated&rdquo; 
              date. We encourage you to review this Privacy Policy periodically for any changes.
            </p>
          </section>
        </div>

        {/* Back to Home Button */}
        <div className="mt-12 text-center">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Home
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-zinc-800 mt-12 py-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-zinc-500 text-sm">
          <p>© 2025 T-Fit Inc Classes. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
